<template>
  <BaseFormLayout
    :title="`${editMode ? 'Edit' : 'Add'} ${announcementModule.announcement}`"
    :nonFieldErrors="formErrors.non_field_errors"
  >
    <template #actions>
      <PrimaryButton
        id="announcementReset"
        v-if="!editMode"
        @click="resetForm()"
        btnClass="mr-2 text-uppercase rounded-lg"
        name="Reset"
      >
        Reset
      </PrimaryButton>
      <SecondaryButton
        id="announcementSubmit"
        btnClass="text-uppercase rounded-lg"
        name="Submit"
        @click="submitForm()"
        :disabled="!isValid"
      >
      </SecondaryButton>
    </template>
    <template #content>
      <v-form
        v-model="isValid"
        ref="announcementForm"
        :key="formCount"
        @submit.prevent="submitForm"
      >
        <v-card elevation="0" class="pa-4 rounded-lg">
          <v-row class="ma-0">
            <v-col cols="12" md="3" lg="3">
              <SelectField
                id="visibility"
                :multiple="true"
                v-model="announcementDetails.visibility"
                name="visibility"
                :itemsList="userRoleList"
                :rules="[(val) => !!val || 'Announcement for is required']"
                label="Announcement for *"
                @change="delete formErrors.visibility"
              />
            </v-col>
            <v-col cols="12" md="3" lg="3">
              <InputField
                id="start_date"
                type="date"
                v-model="announcementDetails.start_date"
                name="start_date"
                :min="returnToday()"
                :rules="[(val) => minimumToday(val)]"
                label="Start Date"
                :required="true"
                @input="delete formErrors.start_date"
              />
            </v-col>
            <v-col cols="12" md="2" lg="2">
              <InputField
                type="time"
                id="start_time"
                v-model="announcementDetails.start_time"
                name="start_time"
                label="Start Time"
                :required="true"
                @input="delete formErrors.start_time"
              />
            </v-col>
            <v-col cols="12" md="2" lg="2" class="d-flex align-center">
              <SwitchField
                v-model="announcementDetails.verify_group_via_email"
                label="Notify via E-mail"
                @change="emailFieldChanged"
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
              lg="3"
              v-if="announcementDetails.verify_group_via_email"
              class="d-flex align-center"
            >
              <v-form ref="emails" class="pa-0" @submit.prevent="">
                <InputField
                  id="emails"
                  v-model="additionalEmail"
                  label="Additional Email"
                  name="additional_email"
                  persistent-hint
                  :rules="[
                    (v) => !v || emailValidation(v) || 'Invalid email id',
                  ]"
                  hint="Enter Email & press enter to add it to the list"
                  :error-messages="formErrors.additional_email"
                  @keydown.enter="
                    addEmail();
                    delete formErrors.additional_email;
                  "
                />
              </v-form>
            </v-col>
            <v-col cols="9" v-if="announcementDetails.verify_group_via_email">
              <div>
                <v-chip
                  v-for="(elem, i) in additionalEmailList"
                  :key="i"
                  class="ma-2"
                  color="primary"
                  close
                  @click:close="removeEmailFromList(i)"
                >
                  {{ elem }}
                </v-chip>
              </div>
            </v-col>
          </v-row>
        </v-card>
        <v-row class="mt-2">
          <v-col cols="12" md="6">
            <v-card elevation="0" class="rounded-lg" min-height="55vh">
              <v-card-title>
                <span class="text-body-1">Announcement Details</span>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="pa-4">
                <v-row>
                  <v-col cols="12">
                    <InputField
                      id="title"
                      v-model="announcementDetails.title"
                      name="title"
                      label="Title"
                      :required="true"
                    />
                  </v-col>
                  <v-col cols="12">
                    <span>Description *</span>
                    <VueEditor
                      :editorToolbar="customToolbar"
                      v-model="announcementDetails.description"
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card elevation="0" class="rounded-lg" min-height="55vh">
              <v-card-title>
                <span class="text-body-1">Preview</span>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="pa-4">
                <v-row>
                  <v-col cols="12">
                    <span class="text-h6 text-weight-bold pb-12">{{
                      announcementDetails.title
                    }}</span>
                  </v-col>
                  <v-col cols="12">
                    <span v-html="announcementDetails.description" />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
    </template>
  </BaseFormLayout>
</template>

<script>
import BaseFormLayout from "@/components/LayoutComponents/BaseFormLayout.vue";
import { announcementModule } from "@/utils/moduleMapping";
import { VueEditor } from "vue2-editor";
import { minimumToday } from "@/utils/validation";
import { returnToday, emailValidation } from "@/utils/functions";
import { bus } from "@/main";

export default {
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      minimumToday,
      emailValidation,
      returnToday,
      announcementModule,
      formErrors: {},
      isValid: false,
      formCount: 0,
      userRoleList: [
        {
          text: "Account Admin",
          value: "account_admin",
        },
        {
          text: "Account Manager",
          value: "account_manager",
        },
        {
          text: "Normal User",
          value: "normal_user",
        },
        {
          text: "Support User",
          value: "support_user",
        },
        {
          text: "Consignee User",
          value: "consignee_user",
        },
      ],
      announcementDetails: {
        verify_group_via_email: false,
        visibility: [],
      },
      customToolbar: [
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["image", "code-block"],
      ],
      additionalEmail: null,
      additionalEmailList: [],
    };
  },
  components: { BaseFormLayout, VueEditor },
  methods: {
    emailFieldChanged(e) {
      if (e) {
        this.additionalEmailList = [];
      }
    },
    addEmail() {
      if (this.additionalEmail && this.$refs.emails.validate()) {
        let v = this.additionalEmail;
        this.additionalEmailList.push(v);
        this.additionalEmail = null;
      }
    },
    removeEmailFromList(index) {
      let arr = [...this.additionalEmailList];
      arr.splice(index, 1);
      this.additionalEmailList = [...arr];
      delete this.formErrors.additional_email;
    },
    resetForm() {
      this.announcementDetails = {
        verify_group_via_email: false,
        visibility: [],
      };
      this.additionalEmail = null;
      this.$refs.announcementForm.resetValidation();
    },
    submitForm() {
      let payload = {
        ...this.announcementDetails,
      };
      if (this.announcementDetails.verify_group_via_email) {
        payload = {
          ...payload,
          additional_email: [...this.additionalEmailList],
        };
      }
      if (this.editMode) {
        bus.$emit("showLoader", true);
        this.$api.announcement
          .updateAnnouncementObject(payload)
          .then(() => {
            bus.$emit("showLoader", false);
            this.$router.back();
          })
          .catch((err) => {
            this.formErrors = err.data;
            bus.$emit("showLoader", false);
          });
      } else {
        bus.$emit("showLoader", true);
        this.$api.announcement
          .createAnnouncementObject(payload)
          .then(() => {
            bus.$emit("showLoader", false);
            this.$router.back();
          })
          .catch((err) => {
            this.formErrors = err.data;
            bus.$emit("showLoader", false);
          });
      }
    },
    getAnnouncementObject(id) {
      bus.$emit("showLoader", true);
      this.$api.announcement
        .getAnnouncementObject(id)
        .then((res) => {
          this.announcementDetails = res.data;
          this.additionalEmailList = [...res.data.additional_email];
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
  },
  mounted() {
    if (this.$route && this.$route.params && this.$route.params.id) {
      this.getAnnouncementObject(this.$route.params.id);
    }
  },
};
</script>

<style></style>
